import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    IntegrationExportEntity,
    JobStatusType,
    OrthoImageWithContext,
    scanAreaDefaultHexFillColor,
    scanAreaDefaultHexOutlineColor,
    Licenses,
    IntterraImageProduct,
} from "@nova/constants"
import { download, downloadUri } from "tools/browser/download"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { config } from "@nova/configuration/browser"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import {
    DownloadButtonData,
    IntegrationOption,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { get } from "nodash"
import { flightpathTransformer } from "integrations/geojson/transformers/flightpath"
import { useCallback } from "react"
import { useIntterraTransfer } from "integrations/intterra/transfer"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { OrthoFlight, OrthoImage, OrthoJob } from "@nova/protobuf"
import {
    selectWorkspaceDateFormat,
    selectActiveWorkspaceId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { updateOrthoFlight } from "features/pubsub/entityActions/flights/ortho"
import { selectUserID } from "user/profile/selectors"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { AGOLSettings } from "@nova/db-table-integration-authorizations"
import { useExportToAGOL } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/useExportToAGOL"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { getDate } from "features/map/inspector-panel/module"
import { TimeFormat } from "@nova/format"
import { demPath, finished, outputFolder, sampleOutputFolder } from "@nova/util-s3"
import { fetchApiGateway, getApiGatewayDomain } from "@nova/fetch-api-gateway"
import { useCopyResourceLink } from "plugins/shared/useCopyResourceLink"
import { useExportToTAK } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/tak/useExportToTAK"
import { createS3DownloadUrlInBrowser } from "plugins/shared/s3url"
import { UploadState } from "@nova/slice-multifile-upload"
import { OrthoState } from "./OrthomosaicSlice"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import { convertColourToKMLColour } from "@nova/util-strings"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { createPolygonKML } from "@nova/data-create-polygon-kml"
import { FeatureCollection } from "geojson"
import { projectToCRS } from "@nova/calculate-geospatial"
import { getTransformer } from "@nova/transformers"
import { selectProject } from "features/entity-caching/project/module"
import { selectLastSelectedLayer } from "panels/content/side-panel/slice.new"
import { IntegrationType } from "@nova/schemas"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useDataTransfer } from "integrations/transfer/useDataTransfer"

// this is directly tied to the inspector panel
export const useDownloadButtonData = (
    uploads: (OrthoImageWithContext | UploadState<OrthoState>)[]
): DownloadButtonData => {
    const dispatch = useAppDispatch()
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const project = useAppSelector(selectProject)
    const flight = useAppSelector(selectLastSelectedLayer) as OrthoFlight
    const userId = useAppSelector(selectUserID)
    const fire = useAppSelector(selectFireById(fireId))!
    const { organizationUsesIntterra, intterraIntegration, organizationSettingCRS } = config.use(
        "organizationUsesIntterra",
        "intterraIntegration",
        "organizationSettingCRS"
    )
    const { transfer, integrationName } = useDataTransfer()
    const job = flight?.job
    const { transferIntterraData } = useIntterraTransfer()
    const { data: integrationAuthorizations, invalidateAuthorizationsCache } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.agol,
            userId,
        })

    const { data: takAuths, invalidateAuthorizationsCache: invalidateTAK } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.tak,
            authType: "certificate",
            userId,
        })

    const { exportToAGOL } = useExportToAGOL()
    const { exportToTAK } = useExportToTAK()

    const transformer = getTransformer(project).orthoFlight(flight.id)

    const getFilename = useCallback(
        (end: string) => `${fire.name} ${flight.name} - ${end}`,
        [fire, flight]
    )

    const mask = useAppSelector(selectOrthoMaskByFlightId(flight.id))

    const cogId = flight?.cogOrtho?.fileId
    const isDemo = !!flight.demoId
    const folder = isDemo
        ? sampleOutputFolder(flight.demoId)
        : outputFolder(workspaceId, fireId, flight.id)

    const downloadFlightPathKML = () => {
        if (!transformer) throw new Error("Failed to get transformer for flight id: " + flight.id)
        const kml = transformer.flightPathKML()
        if (!kml) throw new Error("Failed to generate flight path kml")

        download(getFilename("Flightpath.kml"), "application/vnd.google-earth.kml+xml", kml)
    }

    const downloadTiff = async () => {
        const filename = getFilename("Orthophoto.tif")
        const s3Key = finished(folder, undefined, undefined, cogId)
        const url = await createS3DownloadUrlInBrowser(s3Key, isDemo)
        await downloadUri(filename, url)
    }
    const downloadLRTiff = async () => {
        const filename = getFilename("Orthophoto_Compressed.tif")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "tif", true), isDemo)
        await downloadUri(filename, url)
    }
    const downloadDEM = async () => {
        const filename = getFilename("DEM.tif")
        const url = await createS3DownloadUrlInBrowser(demPath(folder), isDemo)
        await downloadUri(filename, url)
    }
    const downloadMediumJpg = async () => {
        const filename = getFilename("Orthophoto_MR.jpg")
        const url = await createS3DownloadUrlInBrowser(
            `${folder}/odm_orthophoto/odm_orthophoto_MR.jpg`,
            isDemo
        )
        await downloadUri(filename, url)
    }
    const downloadJpg = async () => {
        const filename = getFilename("Orthophoto.jpg")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "jpg"), isDemo)
        await downloadUri(filename, url)
    }
    const downloadPng = async () => {
        const filename = getFilename("Orthophoto.png")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "png"), isDemo)
        await downloadUri(filename, url)
    }

    const downloadKmz = async () => {
        const filename = getFilename("Orthophoto.kmz")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "kmz"), isDemo)
        await downloadUri(filename, url)
    }

    const downloadCogExport = async (
        format: "jpeg" | "png" | "webp" | "kmz" | "tiff",
        maskPolygon?: [number, number][],
        maxSize?: number
    ) => {
        const ext = { jpeg: "jpg", png: "png", webp: "webp", kmz: "kmz", tiff: "tif" } as const
        const filename = getFilename(
            maskPolygon ? `OrthophotoCropped.${ext[format]}` : `Orthophoto.${ext[format]}`
        )
        const params = new URLSearchParams({ format, isDemo: String(isDemo), filename })
        if (cogId) params.set("cogId", cogId)
        if (maskPolygon) params.set("polygon", JSON.stringify(maskPolygon))
        if (maxSize) params.set("maxSize", String(maxSize))
        const url = `${getApiGatewayDomain()}/org/${workspaceId}/project/${fireId}/flight/${flight.demoId || flight.id}/cog-export?${params}`
        downloadUri(filename, url)
    }

    const downloadMaskKML = useCallback(() => {
        if (!mask?.polygon) return
        download(
            getFilename("OrthoMask.kml"),
            "application/vnd.google-earth.kml+xml",
            createPolygonKML("orthoMask", [
                {
                    coords: mask.polygon.coordinates.map((c) => [c.longitude, c.latitude]),
                    width: 0,
                    fill: convertColourToKMLColour(scanAreaDefaultHexFillColor),
                    color: convertColourToKMLColour(scanAreaDefaultHexOutlineColor),
                },
            ])
        )
    }, [mask, getFilename])

    const downloadRgbImagesZip = async () => {
        const filename = getFilename("Images.zip")
        const params = new URLSearchParams({ type: "rgb", isDemo: String(isDemo) })
        const res = await fetch(
            `/api/organization/${workspaceId}/project/${fireId}/flight/${flight.demoId || flight.id}/images-zip?${params}`
        )
        if (!res.ok) return
        const blob = await res.blob()
        downloadUri(filename, URL.createObjectURL(blob))
    }

    const downloadFlightpathShp = async () => {
        const res = await fetch(
            "/api/ogr2ogr?crs=" +
                coordinateSystems[organizationSettingCRS].code +
                "&type=" +
                "Flightpath",
            {
                method: "POST",
                body: new File(
                    [
                        new Blob([
                            JSON.stringify(
                                flightpathTransformer(
                                    [
                                        ([...uploads] as OrthoImage[]).sort((a, b) =>
                                            (a.takenAt || "") < (b.takenAt || "")
                                                ? -1
                                                : a.takenAt === b.takenAt
                                                  ? 0
                                                  : 1
                                        ),
                                    ],
                                    coordinateSystems[organizationSettingCRS].proj4
                                )
                            ),
                        ]),
                    ],
                    "input.geojson"
                ),
            }
        )
        const blob = await res.blob()
        await downloadUri(getFilename("Flightpath.zip"), URL.createObjectURL(blob))
    }

    const copyResourceLink = useCopyResourceLink({
        workspaceId,
        fireId,
        flightId: flight.demoId || flight.id,
        isDemo,
    })

    const generateStableKmz = (maskPolygon?: [number, number][]) => {
        const params = new URLSearchParams({
            format: "kmz",
            stable: "true",
            isDemo: String(isDemo),
            maxSize: "2000",
        })
        if (cogId) params.set("cogId", cogId)
        if (maskPolygon) params.set("polygon", JSON.stringify(maskPolygon))
        return fetchApiGateway("get /org/:orgId/project/:projectId/flight/:flightId/cog-export", {
            params: { orgId: workspaceId, projectId: fireId, flightId: flight.demoId || flight.id },
            query: params.toString(),
        })
    }

    const downloadScanAreaPolygonKML = useCallback(() => {
        if (!flight) return
        const scanAreaPolygon = flight.job?.scanArea
        if (!scanAreaPolygon) return
        download(
            getFilename("ScanArea.kml"),
            "application/vnd.google-earth.kml+xml",
            createPolygonKML("scanArea", [
                {
                    coords: scanAreaPolygon.map((p) => [p.longitude, p.latitude]),
                    width: 0,
                    fill: flight.scanAreaFillColor
                        ? convertColourToKMLColour(decodeNumberToColor(flight.scanAreaFillColor))
                        : convertColourToKMLColour(scanAreaDefaultHexFillColor),
                    color: flight.scanAreaOutlineColor
                        ? convertColourToKMLColour(decodeNumberToColor(flight.scanAreaOutlineColor))
                        : convertColourToKMLColour(scanAreaDefaultHexOutlineColor),
                },
            ])
        )
    }, [flight, getFilename])

    const downloadShapefile = useCallback(
        async (data: FeatureCollection, type: string, fileName: string) => {
            const res = await fetch(
                "/api/ogr2ogr?crs=" +
                    coordinateSystems[organizationSettingCRS].code +
                    "&type=" +
                    type,
                {
                    method: "POST",
                    body: new File([new Blob([JSON.stringify(data)])], `${type}.geojson`),
                }
            )
            if (res.status >= 300) {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to create shapefile.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 5000,
                    })
                )
            }
            const blob = await res.blob()

            await downloadUri(getFilename(`${fileName}.zip`), URL.createObjectURL(blob))
        },
        [organizationSettingCRS, dispatch, getFilename]
    )

    const downloadMaskShp = useCallback(() => {
        if (!mask?.polygon) return
        downloadShapefile(
            {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        geometry: {
                            type: "Polygon",
                            coordinates: [
                                mask.polygon.coordinates.map((c) =>
                                    projectToCRS(
                                        [c.longitude, c.latitude],
                                        coordinateSystems[organizationSettingCRS].proj4
                                    )
                                ),
                            ],
                        },
                        properties: { id: flight.id },
                    },
                ],
            },
            "Polygon",
            "Ortho Mask"
        )
    }, [downloadShapefile, flight, mask, organizationSettingCRS])

    const downloadScanAreaShp = useCallback(() => {
        if (!flight) return
        const scanAreaPolygon = flight.job?.scanArea
        if (!scanAreaPolygon) return
        downloadShapefile(
            {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        geometry: {
                            type: "Polygon",
                            coordinates: [
                                scanAreaPolygon.map((p) =>
                                    projectToCRS(
                                        [p.longitude, p.latitude],
                                        coordinateSystems[organizationSettingCRS].proj4
                                    )
                                ),
                            ],
                        },
                        properties: {
                            id: flight.id,
                        },
                    },
                ],
            },
            "Polygon",
            "Scan Area"
        )
    }, [downloadShapefile, flight, organizationSettingCRS])

    const baseOptions: NestedDownloadData = {}
    let integrationOptions: Record<string, IntegrationOption> | undefined = undefined

    const hasExtraFeature = (
        job: OrthoJob | undefined,
        extraFeature: string
    ): boolean | undefined => {
        if (!job) return

        const extraFeaturesJSON = get(job, "extraFeatures") as string | undefined
        if (!extraFeaturesJSON) return
        const extraFeatures = JSON.parse(extraFeaturesJSON.replaceAll("'", '"')) as string[]

        return extraFeatures.includes(extraFeature)
    }

    if (job) {
        if (job.status === JobStatusType.COMPLETED)
            baseOptions.Ortho = {
                TIFF: { callback: downloadTiff, restrictedLicenses: [Licenses.Lite] },
            }

        const extraFeaturesJSON = get(job, "extraFeatures") as string | undefined
        if (extraFeaturesJSON) {
            const extraFeatures = JSON.parse(extraFeaturesJSON.replaceAll("'", '"')) as string[]
            // an extra feature for the LR tiff was never made, piggyback the kmz link because it definitely came after LR tiffs started getting generated
            if (extraFeatures.includes("orthoKmzLink")) {
                baseOptions.Ortho = {
                    ...baseOptions.Ortho,
                    ["SMALL TIFF"]: {
                        callback: downloadLRTiff,
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
            }
            // Now treating medium (max 64MP) as the default.
            if (extraFeatures.includes("mediumJpg")) {
                baseOptions.Ortho = {
                    ...baseOptions.Ortho,
                    JPG: {
                        callback: downloadMediumJpg,
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
            }
            // Now treating normal (max 16MP) as "small".
            if (extraFeatures.includes("orthoJpg")) {
                baseOptions.Ortho = {
                    ...baseOptions.Ortho,
                    ["SMALL JPG"]: { callback: downloadJpg, restrictedLicenses: [Licenses.Lite] },
                }
            }
            if (extraFeatures.includes("orthoPng")) {
                baseOptions.Ortho = {
                    ...baseOptions.Ortho,
                    PNG: { callback: downloadPng, restrictedLicenses: [Licenses.Lite] },
                }
            }
            if (extraFeatures.includes("orthoKmz")) {
                baseOptions.Ortho = {
                    ...baseOptions.Ortho,
                    KMZ: {
                        callback: downloadKmz,
                        restrictedLicenses: [Licenses.Lite],
                        ...(extraFeatures.includes("cog")
                            ? {
                                  copyCallback: (id: string) =>
                                      copyResourceLink(id, "arc-ortho.kmz", () =>
                                          generateStableKmz()
                                      ),
                              }
                            : extraFeatures.includes("orthoKmzLink")
                              ? {
                                    copyCallback: (id: string) =>
                                        copyResourceLink(id, "arc-ortho.kmz"),
                                }
                              : {}),
                    },
                }
            }

            if (extraFeatures.includes("orthoDem")) {
                baseOptions.DEM = {
                    TIFF: { callback: downloadDEM, restrictedLicenses: [Licenses.Lite] },
                }
            }

            if (extraFeatures.includes("scanArea")) {
                baseOptions["Scan Area"] = {
                    KML: {
                        callback: downloadScanAreaPolygonKML,
                        restrictedLicenses: [Licenses.Lite],
                    },
                    SHP: { callback: downloadScanAreaShp, restrictedLicenses: [Licenses.Lite] },
                }
            }

            if (extraFeatures.includes("cog")) {
                baseOptions.Ortho = {
                    ["JPG (Medium)"]: {
                        callback: () => downloadCogExport("jpeg", undefined, 8000),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    ...baseOptions.Ortho,
                }
            }

            if (extraFeatures.includes("cog") && cogId) {
                baseOptions.Ortho = {
                    ["SMALL TIFF"]: {
                        callback: () => downloadCogExport("tiff"),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    JPG: {
                        callback: () => downloadCogExport("jpeg"),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    PNG: {
                        callback: () => downloadCogExport("png"),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    KMZ: {
                        callback: () => downloadCogExport("kmz"),
                        copyCallback: (id) =>
                            copyResourceLink(id, "arc-ortho.kmz", () => generateStableKmz()),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    ...baseOptions.Ortho, // existing options win
                }

                if (!baseOptions.Images) {
                    baseOptions.Images = {
                        ZIP: {
                            callback: downloadRgbImagesZip,
                            restrictedLicenses: [Licenses.Lite],
                        },
                    }
                }
            }

            if (extraFeatures.includes("cog") && mask?.polygon) {
                const maskPoly = mask.polygon.coordinates.map((c): [number, number] => [
                    c.longitude,
                    c.latitude,
                ])
                baseOptions["Orthomosaic (Cropped)"] = {
                    ["SMALL TIFF"]: {
                        callback: () => downloadCogExport("tiff", maskPoly),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    JPG: {
                        callback: () => downloadCogExport("jpeg", maskPoly),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    ["JPG (Medium)"]: {
                        callback: () => downloadCogExport("jpeg", maskPoly, 8000),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    PNG: {
                        callback: () => downloadCogExport("png", maskPoly),
                        restrictedLicenses: [Licenses.Lite],
                    },
                    KMZ: {
                        callback: () => downloadCogExport("kmz", maskPoly),
                        copyCallback: (id) =>
                            copyResourceLink(id, "arc-ortho-cropped.kmz", () =>
                                generateStableKmz(maskPoly)
                            ),
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
                baseOptions["Crop Mask"] = {
                    KML: { callback: downloadMaskKML, restrictedLicenses: [Licenses.Lite] },
                    SHP: { callback: downloadMaskShp, restrictedLicenses: [Licenses.Lite] },
                }
            }
        }
    }
    baseOptions["Flight Path"] = {
        KML: { callback: downloadFlightPathKML, restrictedLicenses: [Licenses.Lite] },
        SHP: {
            callback: downloadFlightpathShp,
            restrictedLicenses: [Licenses.Lite],
        },
    }
    /*
     * We store the name of the secret in AWS Secrets manager in Dynamo as a Portfolio setting
     * passed server side, this secret is retrieved and contains the key/secret, and bucket details
     * */
    if (organizationUsesIntterra) {
        if (intterraIntegration) {
            integrationOptions ??= {}
            integrationOptions.Intterra = {
                callback: async () => {
                    if (!flight) throw "No flight"
                    if (!userId) throw "No userId"

                    await transferIntterraData({
                        workspaceId,
                        fireId,
                        flightIds: [flight.id],
                        imageType: IntterraImageProduct.ElectroOpticalColour,
                    })

                    dispatch(
                        updateOrthoFlight({
                            id: flight.id,
                            updates: {},
                            context: {
                                updateIntegrationId: "intterra",
                            },
                        })
                    )
                },
                successMessage: "Sent to Intterra",
                failureMessage: "Failed to connect to Intterra",
                key: "intterra",
            }
        }
    }
    if (integrationName) {
        integrationOptions ??= {}
        integrationOptions[integrationName] = {
            callback: async () => {
                await transfer("rgb", [flight.id])

                dispatch(
                    updateOrthoFlight({
                        id: flight.id,
                        updates: {},
                        context: {
                            updateIntegrationId: "azure",
                        },
                    })
                )
            },
            successMessage: "Transfer job started",
            failureMessage: "Failed to initiate transfer",
            key: "azure",
        }
    }
    if (integrationAuthorizations?.at(0) && fireId && workspaceId) {
        integrationOptions ??= {}
        const settings = integrationAuthorizations.at(0)?.settings as AGOLSettings
        const lastExport = integrationAuthorizations.at(0)?.lastExport?.[flight.id]
        integrationOptions[`Send to ${settings.selectedWebMap?.title ?? "User Content"}`] = {
            callback: async () => {
                if (!flight) throw "No flight"
                return await exportToAGOL({
                    entities: [{ id: flight.id, type: IntegrationExportEntity.OrthoFlight }],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateAuthorizationsCache()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed to connect to AGOL",
            key: "agol",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    if (takAuths?.at(0) && fireId && workspaceId && hasExtraFeature(job, "orthoDem")) {
        integrationOptions ??= {}
        const auth = takAuths.at(0)
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions["Send to TAK"] = {
            callback: async () => {
                if (!auth) return
                if (!flight) {
                    throw "No flight selected"
                }

                return exportToTAK({
                    entities: [{ id: flight.id, type: IntegrationExportEntity.OrthoFlight }],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateTAK()
                })
            },
            successMessage: "Success",
            failureMessage: "Failure",
            key: "tak",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    return {
        exportOptions: baseOptions,
        integrationOptions,
    }
}
